import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import KitLocale from '../../common/utils/KitLocale';
import KitModal from '../KitModal';
import { breakpoints, spacing } from '../../common/theme/constants';
import { TranslateFunc } from '../../common/types';
import {
  getDefaultKitLocalePickerResult,
  getKitLocalePickerResult,
  KitLocalePickerResult,
} from './config';
import { focusTrapDelay } from '../../common/utils/KitCommon';
import { KitFlexCol, KitFlexRow } from '../KitFlexbox';
import KitButton from '../KitButton';

const StyledKitModal = styled(KitModal)`
  .cp-locale-picker {
    width: 90%;
    min-width: 700px;
    max-width: 800px;
    @media all and (max-width: ${breakpoints.sm}px) {
      width: auto;
      max-width: inherit;
      min-width: inherit;
    }
  }
`;

const SectionContainer = styled.div`
  padding: 0 ${spacing.absolute.s}px;
  flex: 1;
`;

const SectionTitle = styled.h6`
  font-size: ${({ theme }) => theme.headingFont.fontSize.h6}rem;
  padding: 0;
  margin: 0;
`;

const LocaleList = styled(KitFlexRow)`
  flex-wrap: wrap;
  margin: ${spacing.m}rem 0;
`;

const LocaleItem = styled.li`
  padding 0 0 ${spacing.xs}rem 0;
  list-style: none;
`;

const LocalItemButton = styled(KitButton)`
  min-width: unset;
`;

export interface KitLocalePickerProps extends PropsWithChildren {
  animation?: boolean;
  show: boolean;
  onSelect: (result: KitLocalePickerResult) => void;
  onHide: () => void;
  t: TranslateFunc;
  regionCode?: string;
  supportedLocales?: string[];
}

export interface KitLocalePickerWithStaticMethods<T> extends React.FC<T> {
  getDefaultKitLocalePickerResult: (t: TranslateFunc) => KitLocalePickerResult;
  getKitLocalePickerResult: (
    t: TranslateFunc,
    regionCode: string,
    localeCode: string
  ) => KitLocalePickerResult;
}

/**
 * Mockup:
 * https://chargepoint.ent.box.com/s/ki7uo1h42az22s4kxn1ckxpm054uo9jv
 *
 * @param props
 * @param strings
 */
const KitLocalePicker: KitLocalePickerWithStaticMethods<KitLocalePickerProps> = (props) => {
  const config = KitLocale.getKitLocaleConfig(props.t);

  const onRegionLocaleClicked = (regionCode: string, localeCode: string) => {
    const result = getKitLocalePickerResult(props.t, regionCode, localeCode);
    if (!result) {
      console.error('Failed to find locale picker result for', regionCode, localeCode);
      return;
    }
    props.onSelect(result);
  };

  return (
    <>
      {props.children}
      <StyledKitModal
        show={props.show}
        onHide={props.onHide}
        dialogClassName='cp-locale-picker'
        animation={props.animation}
        checkCanFocusTrap={focusTrapDelay(200)}
      >
        <KitModal.Header closeButton t={props.t}>
          <KitModal.Title>{props.t('kit:locale_picker.title')}</KitModal.Title>
        </KitModal.Header>
        <KitModal.Body>
          <KitFlexCol>
            {config.regions.map((region) =>
              !props.regionCode || props.regionCode === region.regionCode ? (
                <SectionContainer key={region.regionCode}>
                  <SectionTitle id={`region_${region.regionCode}`}>
                    {region.regionName}
                  </SectionTitle>
                  <LocaleList role='group' aria-describedby={`region_${region.regionCode}`}>
                    {region.locales.map((locale) =>
                      !props.supportedLocales ||
                      props.supportedLocales.find((l) => {
                        return l === locale.localeCode;
                      }) ? (
                        <LocalItemButton
                          variant='link'
                          lang={locale.localeCode}
                          key={locale.localeCode}
                          onClick={() =>
                            onRegionLocaleClicked(region.regionCode, locale.localeCode)
                          }
                        >
                          {locale.localizedLanguageName}
                        </LocalItemButton>
                      ) : undefined
                    )}
                  </LocaleList>
                </SectionContainer>
              ) : undefined
            )}
          </KitFlexCol>
        </KitModal.Body>
      </StyledKitModal>
    </>
  );
};

KitLocalePicker.getKitLocalePickerResult = getKitLocalePickerResult;
KitLocalePicker.getDefaultKitLocalePickerResult = getDefaultKitLocalePickerResult;

export default KitLocalePicker;
